/**
 * The art an item falls back to when it has none of its own.
 *
 * Janeks: *"each item category has an icon."* The category icons are
 * Higgsfield-generated and live in `assets/icons/cat_<name>_icon*.webp`
 * (`assets/generated_items/build_category_icons.py`). `pictureHtml` uses this
 * only when an item's own `iconPath` is missing or does not resolve, so an item
 * with real art is untouched and an item with none shows its KIND rather than
 * a blank cell or its name in text.
 */
export type CategoryIcon =
  | 'headgear' | 'torso' | 'legs' | 'gloves' | 'feet' | 'vest' | 'melee' | 'firearm'
  | 'food' | 'drink' | 'meds' | 'material' | 'tool' | 'junk' | 'backpack';

export interface CategoryProbe {
  kind?: string | null;
  slot?: string | null;
  /** The ItemDef key — only used to tell food from drink from medicine, and a vest from a jacket. */
  key?: string | null;
}

export function categoryIconFor({ kind, slot, key }: CategoryProbe): CategoryIcon {
  const k = (key ?? '').toLowerCase();
  if (slot === 'HEADGEAR') return 'headgear';
  if (slot === 'VEST') return 'vest';
  if (slot === 'TORSO') return /vest|plate/.test(k) ? 'vest' : 'torso';
  if (slot === 'LEGS') return 'legs';
  if (slot === 'GLOVES') return 'gloves';
  if (slot === 'FEET') return 'feet';
  if (slot === 'BACKPACK') return 'backpack';
  switch (kind) {
    case 'MELEE': return 'melee';
    case 'FIREARM': return 'firearm';
    case 'AMMO': return 'material';
    case 'TOOL': return 'tool';
    case 'JUNK': return 'junk';
    case 'MATERIAL': return 'material';
    case 'ARMOUR': return /vest|plate/.test(k) ? 'vest' : 'torso';
    case 'CONSUMABLE':
      if (/water|bottle|drink|juice|cola|tea|coffee|milk/.test(k)) return 'drink';
      if (/bandage|medkit|pill|antibio|first_aid|syringe|splint|gauze|med/.test(k)) return 'meds';
      return 'food';
    default: return 'junk';
  }
}

/** The bare filename `pictureHtml` resolves through the icon map. */
export function categoryIconPath(probe: CategoryProbe): string {
  return `cat_${categoryIconFor(probe)}_icon.webp`;
}
